import { z } from 'zod';
import { UserRoleSchema } from './auth.js';
import { SubscriptionPlanIdSchema, SubscriptionSummarySchema } from './billing.js';
import {
  EmailSendCountsSchema,
  EmailSendStatusSchema,
  EmailSendTargetSchema,
} from './email-sending.js';
import { WorkspaceRoleSchema, WorkspaceNameSchema } from './workspaces.js';

// ─────────────────────────────────────────────────────────────────
// System administration (platform level)
//
// BlogForger runs in private beta without a public signup form: accounts are
// created by a **platform admin** (`users.is_platform_admin`), which is a
// different axis from the per-workspace `WorkspaceRole` and from the content
// `UserRole`. A platform admin can provision an account and assign a plan to
// a workspace; everything else about a workspace stays with its owner.
//
// The first platform admin is created by the first-run wizard
// (`/api/setup`, see InstanceSetupSchema below) — the only unauthenticated
// write in the API, and it shuts itself off as soon as one exists.
// ─────────────────────────────────────────────────────────────────

export const AdminCreateUserSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(200),
  /**
   * Content role granted to the new account. Defaults to `admin`: the account
   * is the owner of the workspace created with it, and site settings / member
   * management are gated on the *content* role in several routes, so anything
   * lower could not finish its own onboarding.
   */
  role: UserRoleSchema.default('admin'),
  /**
   * Initial password. When omitted the server generates one and returns it
   * **once** in the response — nothing stores it in plaintext, so an
   * unrecorded password means resetting the account, not re-reading it.
   */
  password: z.string().min(8).max(100).optional(),
  /** Name of the workspace created for this user. Defaults to "<name>'s workspace". */
  workspaceName: WorkspaceNameSchema.optional(),
  /**
   * Optional plan to assign to the new workspace up front (comped beta
   * accounts). `periodDays` null/omitted means the plan does not expire.
   */
  plan: SubscriptionPlanIdSchema.optional(),
  periodDays: z.number().int().min(1).max(3650).nullish(),
});
export type AdminCreateUserInput = z.infer<typeof AdminCreateUserSchema>;

export const AdminWorkspaceRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: WorkspaceRoleSchema,
  needsSetup: z.boolean(),
});
export type AdminWorkspaceRef = z.infer<typeof AdminWorkspaceRefSchema>;

export const AdminUserSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: UserRoleSchema,
  isActive: z.boolean(),
  isPlatformAdmin: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  /** Workspaces this user belongs to, with their role in each. */
  workspaces: z.array(AdminWorkspaceRefSchema),
});
export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;

export const AdminCreateUserResultSchema = z.object({
  user: AdminUserSummarySchema,
  workspace: AdminWorkspaceRefSchema,
  /**
   * Generated initial password, returned only on this response. Present when
   * the request did not supply one.
   */
  generatedPassword: z.string().nullable(),
});
export type AdminCreateUserResult = z.infer<typeof AdminCreateUserResultSchema>;

export const AdminWorkspaceSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  ownerEmail: z.string().nullable(),
  ownerName: z.string().nullable(),
  memberCount: z.number().int(),
  plan: SubscriptionPlanIdSchema,
  subscriptionStatus: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  /** True while unpaid, so the admin can see who still owes/is unplanned. */
  needsSetup: z.boolean(),
});
export type AdminWorkspaceSummary = z.infer<typeof AdminWorkspaceSummarySchema>;

export const AdminPaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Case-insensitive match on name/email (users) or name (workspaces). */
  search: z.string().trim().max(200).optional(),
});
export type AdminPaginationQuery = z.infer<typeof AdminPaginationQuerySchema>;

export const AdminUsersResultSchema = z.object({
  users: z.array(AdminUserSummarySchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type AdminUsersResult = z.infer<typeof AdminUsersResultSchema>;

export const AdminWorkspacesResultSchema = z.object({
  workspaces: z.array(AdminWorkspaceSummarySchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type AdminWorkspacesResult = z.infer<typeof AdminWorkspacesResultSchema>;

export const AdminOverviewSchema = z.object({
  users: z.object({
    total: z.number().int(),
    active: z.number().int(),
    platformAdmins: z.number().int(),
    /** Accounts that have never signed in — invites still pending acceptance. */
    neverSignedIn: z.number().int(),
  }),
  workspaces: z.object({
    total: z.number().int(),
    awaitingSetup: z.number().int(),
    onPaidPlan: z.number().int(),
  }),
});
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;

// ─────────────────────────────────────────────────────────────────
// Send metrics (platform level)
//
// Deliberately **counts and a timeline, never recipients**. A send's
// `email_recipients` rows carry the audience's addresses, their per-recipient
// merge values and the rendered body; an operator watching delivery health
// needs none of that, and reading it would make the platform the processor of
// record for data it has no purpose for. So this surface exposes:
//
//   * the send's own timeline (queued → started → finished/called off)
//   * tallies per delivery outcome
//   * one identifying label (the email's subject) and the audience names
//
// It deliberately does **not** expose `email_recipients.errorMessage`: SMTP
// rejection text routinely quotes the rejected address, so a "failure reason"
// column would smuggle recipient PII through a counts-only screen.
// ─────────────────────────────────────────────────────────────────

/**
 * One send's progress. `counts` is the **effective** tally, not the raw rows:
 * for a cancelled send the recipients left queued are moved out of
 * `counts.queued` and into `abandoned`, because a cancelled send never
 * delivers them and reporting them as "still in queue" would read as a
 * backlog that is never going to drain. Rendering `counts` as-is is therefore
 * correct by construction.
 */
export const AdminSendMetricsSchema = z.object({
  id: z.string(),
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  emailId: z.string(),
  /** The email's subject, used to identify the send. Tenant content. */
  emailSubject: z.string().nullable(),
  senderName: z.string().nullable(),
  /** The audiences this send was snapshotted against (list/segment names). */
  targets: z.array(EmailSendTargetSchema),
  status: EmailSendStatusSchema,
  counts: EmailSendCountsSchema,
  /** Queued recipients the cancellation stranded. Always 0 unless cancelled. */
  abandoned: z.number().int(),
  /** Recipients that left the queue: total − queued. */
  attempted: z.number().int(),
  createdAt: z.string(),
  scheduledAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  /** How long the send waited for the job. Null until it was first claimed. */
  queuedMs: z.number().int().nullable(),
  /** How long it has been (or was) running. Null until first claimed. */
  runningMs: z.number().int().nullable(),
  /**
   * Measured delivery rate over the run, in recipients/second. This is the
   * only honest answer to "how did the throttled executor handle it" that
   * snapshot data supports — the executor's own `throttled_runs` history is
   * pruned to its longest rate window (one minute by default), so it cannot
   * be read back after the fact.
   */
  recipientsPerSecond: z.number().nullable(),
});
export type AdminSendMetrics = z.infer<typeof AdminSendMetricsSchema>;

/** Send-level and recipient-level totals across every workspace. */
export const AdminSendMetricsOverviewSchema = z.object({
  /** How many sends sit in each derived status. `cancelled` counts as one. */
  sends: z.object({
    total: z.number().int(),
    queued: z.number().int(),
    sending: z.number().int(),
    sent: z.number().int(),
    failed: z.number().int(),
    cancelled: z.number().int(),
  }),
  recipients: z.object({
    total: z.number().int(),
    delivered: z.number().int(),
    bounced: z.number().int(),
    complained: z.number().int(),
    failed: z.number().int(),
    /** Genuinely waiting to be delivered — excludes cancelled sends. */
    queued: z.number().int(),
    /** Stranded by a cancellation; never delivered, never going to be. */
    abandoned: z.number().int(),
  }),
  /**
   * The throttle the sender job is currently configured with, so a measured
   * `recipientsPerSecond` can be read against its ceiling rather than in a
   * vacuum. The sender job applies one allowance across the whole sweep.
   */
  throttle: z.object({
    perMinute: z.number().int(),
    maxDegreeOfParallelism: z.number().int(),
    batchSize: z.number().int(),
    maxPerRun: z.number().int(),
    maxSendsPerRun: z.number().int(),
  }),
});
export type AdminSendMetricsOverview = z.infer<typeof AdminSendMetricsOverviewSchema>;

export const AdminSendMetricsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Narrow to one workspace. Omitted means every workspace. */
  workspaceId: z.string().uuid().optional(),
  /** Narrow to one derived status. Omitted means every status. */
  status: EmailSendStatusSchema.optional(),
});
export type AdminSendMetricsQuery = z.infer<typeof AdminSendMetricsQuerySchema>;

export const AdminSendMetricsResultSchema = z.object({
  sends: z.array(AdminSendMetricsSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type AdminSendMetricsResult = z.infer<typeof AdminSendMetricsResultSchema>;

/**
 * Assign a plan to a workspace without a payment provider — the comped /
 * manual-term path a private beta needs (there is otherwise no way to put a
 * workspace on a paid plan except a live Paystack checkout).
 *
 * `plan: 'free'` cancels any live subscription, returning the workspace to
 * the default entitlement. `periodDays` omitted/null means no expiry; a
 * number sets `current_period_end` and the existing expiry worker ends the
 * term when it passes.
 */
export const AdminSetWorkspacePlanSchema = z.object({
  plan: SubscriptionPlanIdSchema,
  periodDays: z.number().int().min(1).max(3650).nullish(),
});
export type AdminSetWorkspacePlanInput = z.infer<typeof AdminSetWorkspacePlanSchema>;

export const AdminSetWorkspacePlanResultSchema = z.object({
  workspaceId: z.string().uuid(),
  plan: SubscriptionPlanIdSchema,
  subscription: SubscriptionSummarySchema.nullable(),
});
export type AdminSetWorkspacePlanResult = z.infer<typeof AdminSetWorkspacePlanResultSchema>;

// ─────────────────────────────────────────────────────────────────
// First-run instance setup
//
// The README promises a setup wizard on first boot; this is it. It replaces
// the seed dependency for Docker users (the seed still works for local dev)
// and is the reason private beta can have no signup form at all.
// ─────────────────────────────────────────────────────────────────

export const InstanceSetupStatusSchema = z.object({
  /** True while no platform admin exists — i.e. the instance is unclaimed. */
  needsSetup: z.boolean(),
  /**
   * Whether the setup endpoint is open at all (`SETUP_ENABLED`, default true).
   * An operator who pre-provisioned the instance closes it, so `needsSetup`
   * can be true while claiming is refused.
   */
  setupAllowed: z.boolean(),
});
export type InstanceSetupStatus = z.infer<typeof InstanceSetupStatusSchema>;

export const InstanceSetupSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(200),
  password: z
    .string()
    .min(8)
    .max(100)
    .describe('At least 8 characters. Stored as a bcrypt hash; never returned.'),
  /** Name of the first workspace. Defaults to "<name>'s workspace". */
  workspaceName: WorkspaceNameSchema.optional(),
});
export type InstanceSetupInput = z.infer<typeof InstanceSetupSchema>;

export const InstanceSetupResultSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: UserRoleSchema,
    isPlatformAdmin: z.literal(true),
  }),
  workspace: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  /** The new admin is signed in by the same request — see POST /api/setup. */
  refreshToken: z.string(),
  /** Ready-made URL the new admin lands on. */
  nextUrl: z.string(),
  /**
   * Deployment baseline this request installed (idempotent): the bundled
   * theme, without which a workspace cannot have site settings, plus the
   * default fonts. Reported so the wizard can explain why its theme step is
   * already populated on a fresh install.
   */
  provisioned: z.object({
    themeId: z.string().nullable(),
    themeName: z.string().nullable(),
    /** Fonts added by this request; 0 when they were already installed. */
    fonts: z.number().int(),
  }),
});
export type InstanceSetupResult = z.infer<typeof InstanceSetupResultSchema>;
